import React from 'react';
import { MembershipLevel } from '@lib/types';
import { Button, Icon, Typography, Divider, Box } from '@ace-clublabs/shared-component-library';
import { useGlobalState } from '../shared/state';
import { AppFlow } from '@lib/types';
import { useNavigate } from 'react-router-dom';
import { l } from 'vite/dist/node/types.d-aGj9QkWt';

interface Props {
  amountDue: number;
  onClickWithAutoPay: () => void;
  membershipLevel: MembershipLevel | '';
  taxAmount: number;
  preDiscountAmountDue: number;
  productCost: number;
  onClickWithoutAutoPay?: () => void;
  eligible?: boolean;
  discount?: number;
  membershipProductRV?: string;
}

const getDollarString = (amount: number) => {
  return `$${amount.toFixed(2)}`;
};

const PaymentCard: React.FunctionComponent<Props> = ({
  amountDue,
  onClickWithAutoPay,
  membershipLevel,
  eligible,
  discount,
  productCost,
  taxAmount,
  onClickWithoutAutoPay,
  preDiscountAmountDue,
  membershipProductRV,
}) => {
  const [appFlow] = useGlobalState('appFlow');
  const navigate = useNavigate();
  const title = 'Amount due';
  let label = '' as any;
  if (membershipLevel) {
    if (membershipProductRV && membershipProductRV === 'Y') {
      label = ' RV';
    }
    label += ' member';
    label = (
      <>
        {membershipLevel}
        {membershipLevel !== MembershipLevel.Classic ? <sup>&reg;</sup> : null}
        {label}
      </>
    );
  } else {
    label = 'AAA member';
  }

  const membershipLevelLabel = appFlow === AppFlow.AUTOPAY ? 'Minimum due' : label;
  const totalAmountDue = discount ? amountDue : preDiscountAmountDue;

  return (
    <Box className="payment-card" data-quid="payment-card">
      <Typography variant="b1Medium" component="h2" className="payment-card-title">
        {title}
      </Typography>
      <Box className="payment-card-line-item-container">
        <Box className="payment-card-membership payment-line-item" sx={{ mt: 2 }}>
          <Typography variant="b2Regular" component="span" className="membership-label" id="membership-level-label">
            {membershipLevelLabel}
          </Typography>
          <Typography variant="b2Regular" component="span" className="membership-amount">
            {getDollarString(productCost)}
          </Typography>
        </Box>
        {eligible && discount && (
          <>
            <Box
              className="payment-card-membership payment-card-upgrade payment-line-item"
              data-quid="payment-card-upgrade"
              sx={{ mt: 2 }}
            >
              <Box className="payment-card-icon-container">
                <Typography variant="b2Regular" component="span" className="discount-tag">
                  <Icon iconName="tag-solid" className="discount-tag-icon" />
                </Typography>
                <Typography variant="b2Regular" component="span" className="discount-label">
                  Auto Pay savings
                </Typography>
              </Box>
              <Typography variant="b2Regular" component="span" className="payment-card-discount">
                {`-${getDollarString(discount)}`}
              </Typography>
            </Box>
            <Typography
              variant="b3Regular"
              component="div"
              className="payment-card-required"
              data-quid="payment-card-required"
            >
              Auto Pay enrollment is required for discount
            </Typography>
          </>
        )}
      </Box>
      {!!taxAmount && (
        <Box className="payment-card-membership payment-line-item" sx={{ mt: 2 }}>
          <Typography variant="b2Regular" component="span" className="tax-label">
            Sales Tax
          </Typography>
          <Typography variant="b2Regular" component="span" className="tax-amount">
            {getDollarString(taxAmount)}
          </Typography>
        </Box>
      )}

      <Divider sx={{ mt: 2 }} />

      <Box className="payment-card-container" sx={{ mt: 2 }}>
        <Box className="payment-card-total payment-line-item">
          <Typography variant="b2Bold" component="span">
            Total
          </Typography>
          <Typography variant="b2Bold" component="span" className="total-amount">
            {getDollarString(totalAmountDue)}
          </Typography>
        </Box>
      </Box>
      <Box className="payment-card-btn" sx={{ mt: 2 }}>
        {appFlow === AppFlow.PAYMENT &&
          (eligible ? (
            discount ? (
              <>
                <Button
                  id="autopay-btn"
                  onClick={onClickWithAutoPay}
                  label="Continue with Auto Pay"
                  fullWidth
                  sx={{ mb: 1 }}
                />
                <Button
                  id="skip-btn"
                  onClick={onClickWithoutAutoPay}
                  label="Skip and pay"
                  variant="outlined"
                  fullWidth
                />
              </>
            ) : (
              <Button id="autopay-btn" onClick={onClickWithAutoPay} label="Continue" fullWidth sx={{ mb: 1 }} />
            )
          ) : (
            <Button id="continue-btn" onClick={onClickWithoutAutoPay} label="Continue" fullWidth />
          ))}
        {appFlow === AppFlow.AUTOPAY && (
          <>
            <Button id="continue-btn" onClick={onClickWithAutoPay} label="Continue" fullWidth sx={{ mb: 1 }} />
            <Button id="cancel-btn" onClick={navigate(-1)} label="Cancel" variant="outlined" fullWidth />
          </>
        )}
      </Box>
    </Box>
  );
};

export default PaymentCard;
